import React, { useState, useEffect, useRef } from "react";
import { GET_FILE_API, GET_GLOBAL_COUNTRIES_API, UPDATE_CASE_COUNTRY_API, CREATE_CBU_DETAIL_API } from "../../../services/api-config";
import CustomAccordion from "../../common/accordion/custom-accordion";
import { renderDocx, renderImage, renderPdf } from "./pop-types";
import { McIcon } from "@maersk-global/mds-react-wrapper/components-core/mc-icon";
import { useStore } from "../../../store/store.context";

import "./pop-details.scss";

// Added caseNumber prop to build new file URL signature.
const PopDetails = ({ fetchPopDetails, caseNumber, country, countryId, popIndex = 0 }) => {
  const { store } = useStore();
  const [isMounted, setIsMounted] = useState(true);
  const controllerRef = useRef(null);
  const [isEditingCountry, setIsEditingCountry] = useState(false);
  const [countryList, setCountryList] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(country);
  const [selectedCountryId, setSelectedCountryId] = useState(null);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [savingCountry, setSavingCountry] = useState(false);
  const [displayCountry, setDisplayCountry] = useState(country);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalBankAccount, setModalBankAccount] = useState("");
  const [modalCurrency, setModalCurrency] = useState("");
  const [modalBankName, setModalBankName] = useState("");

  // Get user information from sessionStorage
  const updatedUserInfo = typeof window !== 'undefined' ? sessionStorage.getItem('userInfo') : null;
  let parsedUserInfo = {};
  try {
    parsedUserInfo = updatedUserInfo ? JSON.parse(updatedUserInfo) : {};
  } catch (e) {
    parsedUserInfo = {};
  }
  const userEmail = parsedUserInfo?.email || parsedUserInfo?.userEmail || "";

  const {
    documenttype,
    name,
    beneficiaryname,
    beneficiaryaccount,
    paymentamount,
    currencycode,
    payeename,
    payeeaccount,
    proofdocumentpath,
    valuedate,
  } = fetchPopDetails;

  const formatDateForDisplay = (value) => {
    if (!value) return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const displayValueDate = formatDateForDisplay(valuedate);

  useEffect(() => {
    controllerRef.current = new AbortController();
    setIsMounted(true);

    return () => {
      setIsMounted(false);
      if (controllerRef.current) {
        controllerRef.current.abort(); // Abort any pending fetches when the component unmounts
      }
    };
  }, []);

  useEffect(() => {
    setSelectedCountry(country);
    setDisplayCountry(country);
  }, [country]);

  const fetchCountries = async () => {
    setLoadingCountries(true);
    try {
      const response = await fetch(GET_GLOBAL_COUNTRIES_API);
      if (!response.ok) {
        throw new Error("Failed to fetch countries");
      }
      const data = await response.json();
      setCountryList(data.countries || []);
    } catch (error) {
      console.error("Error fetching countries:", error);
      setCountryList([]);
    } finally {
      setLoadingCountries(false);
    }
  };

  const handleEditClick = () => {
    if (!isEditingCountry) {
      fetchCountries();
    }
    setIsEditingCountry(!isEditingCountry);
  };

  const handleCountryChange = (event) => {
    const selectedValue = event.target.value;
    setSelectedCountry(selectedValue);
    
    // Find and store the country_id for the selected country
    const selectedCountryObj = countryList.find(c => c.country_name === selectedValue);
    if (selectedCountryObj) {
      setSelectedCountryId(selectedCountryObj.country_id);
    }
  };

  const handleCancelEdit = () => {
    setSelectedCountry(displayCountry);
    setIsEditingCountry(false);
  };

  const handleSaveCountry = async () => {
    if (!selectedCountryId || !caseNumber) {
      console.error("Missing required data for country update");
      return;
    }

    setSavingCountry(true);
    try {
      const response = await fetch(UPDATE_CASE_COUNTRY_API, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          casenumber: caseNumber,
          countryid: selectedCountryId,
          modifiedBy: null, // Add user information if available
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update country");
      }

      const data = await response.json();
      console.log("Country updated successfully:", data);
      
      // Update the display country to show the new value
      setDisplayCountry(selectedCountry);
      setIsEditingCountry(false);
      
      // Optionally, you can trigger a refresh of the parent component here
      // or show a success message to the user
    } catch (error) {
      console.error("Error updating country:", error);
      alert("Failed to update country. Please try again.");
      // Revert to original country on error
      setSelectedCountry(displayCountry);
    } finally {
      setSavingCountry(false);
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalBankAccount("");
    setModalCurrency("");
    setModalBankName("");
  };

  const handleSaveBankDetails = async () => {
    if (!country) {
      alert("Country information is missing. Please ensure the country is set for this case.");
      return;
    }

    if (!modalBankAccount || !modalCurrency) {
      alert("Please fill in all required fields (Bank Account and Currency).");
      return;
    }

    try {
      const response = await fetch(CREATE_CBU_DETAIL_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          country_name: country,
          bankaccount: modalBankAccount,
          currency: modalCurrency,
          bankname: modalBankName || null,
          modified_by: userEmail || store.lastModifiedBy || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to save bank details");
      }

      const data = await response.json();
      console.log("Bank details saved successfully:", data);
      
      // Close modal after saving
      handleCloseModal();
      
      // Show success notification
      alert(`Bank details saved successfully!`);
      
      // Optionally, you can trigger a refresh of the parent component here
    } catch (error) {
      console.error("Error saving bank details:", error);
      alert(`Failed to save bank details: ${error.message}`);
    }
  };

  const renderDocumentMap = {
    pdf: renderPdf,
    docx: renderDocx,
    png: renderImage,
  jpeg: renderImage,
  jpg: renderImage,
  };

  const renderPopDocuments = (documentName) => {
    if (!documentName) {
      return <p className="error-text">No documents attached</p>;
    }

    const fileExtension = documentName
      .slice(((documentName.lastIndexOf(".") - 1) >>> 0) + 2)
      .toLowerCase();

  // Build new API path including case number
  const safeCase = caseNumber || "";
  const fileUrl = `${GET_FILE_API(safeCase)}/${encodeURIComponent(documentName)}`;

    const renderDocument = renderDocumentMap[fileExtension];

    if (!renderDocument) {
      return (
        <p className="error-text">Unsupported document type: {fileExtension}</p>
      );
    }

    return renderDocument(fileUrl, documentName);
  };

  const renderPaymentDetails = (details) => (
    <div className="details-section">
      {details.map(({ title, data }, index) => (
        <div key={index} className="details-columns">
          <p className="details-title">{title}</p>
          {data.map((detail, index) => (
            <div className="detail-row" key={index}>
              <p className="label">{detail.label}</p>
              {detail.isEditable && displayCountry?.toLowerCase() === "global" ? (
                <div className="country-edit-container">
                  {isEditingCountry ? (
                    <div className="country-edit-mode">
                      <select
                        className="country-dropdown"
                        value={selectedCountry}
                        onChange={handleCountryChange}
                        disabled={loadingCountries}
                      >
                        <option value="">
                          {loadingCountries ? "Loading..." : "Select Country"}
                        </option>
                        {countryList.map((c) => (
                          <option key={c.country_id} value={c.country_name}>
                            {c.country_name}
                          </option>
                        ))}
                      </select>
                      <button
                        className="save-btn"
                        onClick={handleSaveCountry}
                        disabled={!selectedCountry || loadingCountries || savingCountry}
                      >
                        {savingCountry ? "..." : "✓"}
                      </button>
                      <button
                        className="cancel-btn"
                        onClick={handleCancelEdit}
                        disabled={loadingCountries || savingCountry}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div className="country-display-mode">
                      <p className="value">{displayCountry}</p>
                      <McIcon icon="pencil" style={{ cursor: "pointer" }} size="24" onClick={handleEditClick} />
                    </div>
                  )}
                </div>
              ) : (
                <div className="value-with-icon">
                  <p className="value">{detail.isEditable ? displayCountry : detail.value}</p>
                  {detail.showPlusIcon && (
                    <McIcon 
                      icon="plus" 
                      style={{ 
                        cursor: "pointer", 
                        marginLeft: "0.5rem",
                        color: "#003E62",
                        fontWeight: "bold"
                      }} 
                      size="24" 
                      onClick={handleOpenModal}
                      title="Add account details"
                    />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  const paymentDetails = [
    {
      title: "Payer Information",
      data: [
        { label: `Payer Name : `, value: payeename },
        { label: `Payer Account No : `, value: payeeaccount },
        { label: `Country : `, value: country, isEditable: true },
        { label: `Payment Date : `, value: displayValueDate },
      ],
    },
    {
      title: "Beneficiary Information",
      data: [
        { label: `Beneficiary : `, value: beneficiaryname },
        { label: `Bank Account No. (IBAN) : `, value: beneficiaryaccount, showPlusIcon: true },
        { label: `Amount : `, value: paymentamount },
        { label: `POP Currency : `, value: currencycode },
        { label: `Document Type : `, value: documenttype },
       
      ],
    },
  ];

  return (
    <div className="pop-details-container">
      <div className="pop-header">
        <p className="header-text">{`${documenttype.replace(
          /POP/,
          "Attachment"
        )}`}</p>
      </div>
      <hr className="divider" />

      {documenttype === "Non-POP" ? "" : renderPaymentDetails(paymentDetails)}

      <CustomAccordion title={`Qualified POP ${popIndex + 1}`}>
        {isMounted && renderPopDocuments(proofdocumentpath)}
      </CustomAccordion>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add Bank Account Details</h3>
              <McIcon 
                icon="close" 
                style={{ cursor: "pointer" }} 
                size="24" 
                onClick={handleCloseModal} 
              />
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="bankAccount">Bank Account Number *</label>
                <input
                  id="bankAccount"
                  type="text"
                  className="form-input"
                  placeholder="Enter bank account number"
                  value={modalBankAccount}
                  onChange={(e) => setModalBankAccount(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="currency">Currency *</label>
                <input
                  id="currency"
                  type="text"
                  className="form-input"
                  placeholder="Enter currency code (e.g., USD, EUR)"
                  value={modalCurrency}
                  onChange={(e) => setModalCurrency(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="bankName">Beneficiary Account Name (Optional)</label>
                <input
                  id="bankName"
                  type="text"
                  className="form-input"
                  placeholder="Enter beneficiary account name"
                  value={modalBankName}
                  onChange={(e) => setModalBankName(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="modal-cancel-btn"
                onClick={handleCloseModal}
              >
                Cancel
              </button>
              <button
                className="modal-save-btn"
                onClick={handleSaveBankDetails}
                disabled={!modalBankAccount || !modalCurrency}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PopDetails;

 